import { useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ActionableNotification, Button, InlineNotification, Modal, Select, SelectItem, TextArea, TextInput } from '@carbon/react';
import { Add, TrashCan } from '@carbon/icons-react';
import { api, ApiError } from '../../api/client';
import { useAuth } from '../../auth/AuthContext';
import { BlockSkeleton, ErrorState, InlineError, PageHeader } from '../../components/states';
import { When } from '../../components/format';
import { CommandStatus, HE_A_ERRORS } from '../../components/labels';
import { REGION, type LodeCategory } from '../../components/LodeChange';
import type { Command } from '../Commands';
import { MoneyInput } from '../../components/MoneyInput';

type Blocked = { dai: number; ma: string; so: string; lyDo?: string };
type Cap = { dai: number; ma: string; toiDa: number };
type Data = {
  maxMultiple: number; ratesChangedAt: string | null;
  categories: (LodeCategory & { override: number | null; priceOverride: number | null })[];
  stations: { id: number; name: string; region: string }[];
  blocked: Blocked[]; caps: Cap[];
  meta: { rates: { version: number; at: string | null }; limits: { version: number; at: string | null } };
  pendingRates: { id: string; status: string; at: string; maker: string | null; reason: string; rates: Record<string, number>; prices: Record<string, number> }[];
};
type Cat = Data['categories'][number];

const isStale = (e: unknown) => e instanceof ApiError && e.status === 409 && e.code === 'STALE';
const rateText = (s: string) => s.replace(',', '.').replace(/[^\d.]/g, '');
// a rate has at most 2 decimals, a price 1 (he A: the debit stays a whole number of dong)
const badNumber = (v: string, n = 2) => v !== '' && (!(Number(v) > 0) || Number(v) > 1e6 || Math.abs(Math.round(Number(v) * 10 ** n) - Number(v) * 10 ** n) > 1e-6);
// Mien Bac rows are the Sieu toc table too (he A: lode/st.js plays the Mien Bac bet types)
const REGION_TEXT: Record<string, string> = { ...REGION, bac: 'Miền Bắc (cả Siêu tốc)' };

function Rates({ d }: { d: Data }) {
  const { can } = useAuth();
  const nav = useNavigate();
  const qc = useQueryClient();
  // draft values per bet type id: '' = back to the game's value
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [draftGia, setDraftGia] = useState<Record<string, string>>({});
  const [base, setBase] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [reason, setReason] = useState('');
  const [made, setMade] = useState<Command | null>(null);
  const [region, setRegion] = useState('bac');
  const changed = [...new Set([...Object.keys(draftGia), ...Object.keys(draft)])];
  const m = useMutation({
    mutationFn: () => api.put<{ command: Command }>('/api/admin/lode/config/rates', {
      changes: Object.fromEntries(Object.entries(draft).map(([id, v]) => [id, v === '' ? null : Number(v)])),
      prices: Object.fromEntries(Object.entries(draftGia).map(([id, v]) => [id, v === '' ? null : Number(v)])),
      reason, expect: base ?? d.meta.rates.version,
    }),
    onSuccess: (r) => { setMade(r.command); setSaving(false); setDraft({}); setDraftGia({}); setBase(null); qc.invalidateQueries({ queryKey: ['lode-config'] }); },
  });
  const edit = (set: typeof setDraft, cur: Record<string, string>, now: number | null, c: Cat, v: string) => {
    const n = { ...cur };
    if (v === (now != null ? String(now) : '')) delete n[c.id]; else n[c.id] = v;
    set(n);
    const other = set === setDraft ? draftGia : draft;
    setBase(Object.keys(n).length || Object.keys(other).length ? (base ?? d.meta.rates.version) : null);
  };
  const shown = (c: Cat) => ({
    gia: draftGia[c.id] ?? (c.priceOverride != null ? String(c.priceOverride) : ''),
    rate: draft[c.id] ?? (c.override != null ? String(c.override) : ''),
  });
  // the pair after the change: an empty field is the game's value
  const after = (c: Cat) => { const s = shown(c); return { gia: s.gia === '' ? c.pay_number : Number(s.gia), rate: s.rate === '' ? c.rate : Number(s.rate) }; };
  const maxRate = (c: Cat, gia: number) => Math.floor(((c.rate * gia * d.maxMultiple) / c.pay_number) * 100) / 100;
  const overCap = (c: Cat) => { const p = after(c); return p.rate * c.pay_number > c.rate * p.gia * d.maxMultiple * (1 + 1e-12); };
  const byId = new Map(d.categories.map((c) => [String(c.id), c]));
  const invalid = changed.some((id) => { const c = byId.get(id)!; const s = shown(c); return badNumber(s.gia, 1) || badNumber(s.rate) || overCap(c); });
  const editable = can('lode.manage') && !d.pendingRates.length;
  return (
    <section className="section">
      <h2>Giá và tỷ lệ trả</h2>
      <p className="note">Giá là số tiền trả cho 1 điểm (lô 2 số giá 22: 1 điểm 1K trả 22K), tỷ lệ là tiền thắng cho 1 điểm mỗi lần trúng. Giá và tỷ lệ gốc là của game; Miền Bắc cũng là bảng của lô đề siêu tốc. Đặt giá hoặc tỷ lệ riêng: tiền thắng trên mỗi đồng cược (tỷ lệ chia giá) {d.maxMultiple > 1 ? <>tối đa {d.maxMultiple} lần của bảng gốc.</> : 'không cao hơn bảng gốc.'} Đổi áp dụng ngay, không chờ người duyệt. Người chơi đang mở màn cược từ trước khi đổi sẽ bị từ chối lượt cược kế tiếp và được mời mở lại để thấy bảng mới; vé đã đặt giữ giá và tỷ lệ lúc đặt.
        {d.ratesChangedAt ? <> Đổi lần cuối <When v={d.ratesChangedAt} />.</> : null}</p>
      {made ? <ActionableNotification kind={made.status === 'FAILED' ? 'error' : 'info'} inline lowContrast onClose={() => setMade(null)}
        title={made.status === 'FAILED' ? 'Hệ A từ chối lệnh đổi giá / tỷ lệ' : made.status === 'SUCCEEDED' ? 'Đã đổi giá / tỷ lệ' : 'Đã gửi lệnh đổi giá / tỷ lệ, hệ A đang thực hiện'}
        subtitle="Mở lệnh để xem kết quả hệ A trả về." actionButtonLabel="Mở lệnh" onActionButtonClick={() => nav(`/commands/${made.id}`)} /> : null}
      {d.pendingRates.map((p) => (
        <ActionableNotification key={p.id} kind="warning" inline lowContrast hideCloseButton title="Có thay đổi giá / tỷ lệ chưa xong"
          subtitle={`${p.reason}. Đợi lệnh đó xong (hoặc duyệt / từ chối nếu nó còn chờ duyệt từ trước) rồi mới sửa tiếp.`}
          actionButtonLabel="Mở lệnh" onActionButtonClick={() => nav(`/commands/${p.id}`)}>
          <CommandStatus s={p.status} />
        </ActionableNotification>
      ))}
      {isStale(m.error) ? <InlineNotification kind="warning" lowContrast hideCloseButton title="Giá / tỷ lệ vừa được người khác đổi" subtitle="Chưa lưu gì. Bỏ bản nháp, tải lại và sửa lại." /> : null}
      <div className="toolbar-filters">
        <div><Select id="rate-region" labelText="Miền" value={region} onChange={(e) => setRegion(e.target.value)}>
          {Object.entries(REGION_TEXT).map(([k, t]) => <SelectItem key={k} value={k} text={t} />)}
        </Select></div>
      </div>
      <table className="plain vis">
        <thead><tr><th>Loại cược</th><th className="r">Giá gốc</th><th className="r">Giá đang áp dụng</th><th className="r">Giá riêng</th><th className="r">Tỷ lệ gốc</th><th className="r">Tỷ lệ đang áp dụng</th><th className="r">Tỷ lệ riêng</th></tr></thead>
        <tbody>
          {d.categories.filter((c) => c.region === region).map((c) => {
            const v = shown(c);
            const over = overCap(c);
            return (
              <tr key={c.id} className={String(c.id) in draft || String(c.id) in draftGia ? 'is-changed' : undefined}>
                <td className="is-title">{c.name}<div className="muted num">{c.code}</div></td>
                <td className="r num" data-label="Giá gốc">{c.pay_number}</td>
                <td className="r num" data-label="Giá đang áp dụng">{c.gia_ap_dung}</td>
                <td className="r" data-label="Giá riêng">
                  <TextInput id={'price-' + c.id} size="sm" hideLabel labelText={'Giá riêng ' + c.name} inputMode="decimal" className="money-input money-input--short"
                    placeholder="theo game" disabled={!editable} value={v.gia} invalid={badNumber(v.gia, 1)} invalidText="Lớn hơn 0, 1 số lẻ"
                    onChange={(e) => edit(setDraftGia, draftGia, c.priceOverride, c, rateText(e.target.value))} />
                </td>
                <td className="r num" data-label="Tỷ lệ gốc">{c.rate}</td>
                <td className="r num" data-label="Tỷ lệ đang áp dụng">{c.rate_ap_dung}</td>
                <td className="r" data-label="Tỷ lệ riêng">
                  <TextInput id={'rate-' + c.id} size="sm" hideLabel labelText={'Tỷ lệ riêng ' + c.name} inputMode="decimal" className="money-input money-input--short"
                    placeholder="theo game" disabled={!editable} value={v.rate} invalid={badNumber(v.rate) || over}
                    invalidText={over ? `Giá ${after(c).gia} ăn tối đa ${maxRate(c, after(c).gia)}` : 'Lớn hơn 0, 2 số lẻ'}
                    onChange={(e) => edit(setDraft, draft, c.override, c, rateText(e.target.value))} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {changed.length ? (
        <div className="actionbar" role="region" aria-label="Thay đổi chưa lưu">
          <span><span className="num">{changed.length}</span> loại cược đã đổi, chưa lưu</span>
          <Button kind="ghost" size="md" onClick={() => { setDraft({}); setDraftGia({}); setBase(null); m.reset(); }}>Bỏ thay đổi</Button>
          <Button kind="primary" size="md" disabled={invalid} onClick={() => { setReason(''); setSaving(true); }}>Lưu</Button>
        </div>
      ) : null}
      <Modal open={saving} modalHeading={`Đổi giá / tỷ lệ ${changed.length} loại cược`} primaryButtonText="Áp dụng ngay" secondaryButtonText="Hủy"
        primaryButtonDisabled={m.isPending || reason.trim().length < 5} onRequestClose={() => setSaving(false)} onRequestSubmit={() => m.mutate()}>
        <div className="stack">
          <table className="plain">
            <thead><tr><th>Loại</th><th className="r">Giá đang áp dụng</th><th className="r">Giá sau khi đổi</th><th className="r">Tỷ lệ đang áp dụng</th><th className="r">Tỷ lệ sau khi đổi</th></tr></thead>
            <tbody>{changed.map((id) => {
              const c = byId.get(id)!;
              const p = after(c);
              return <tr key={id}><td className="is-title">{c.name} <span className="muted">{REGION_TEXT[c.region]}</span></td>
                <td className="r num">{c.gia_ap_dung}</td><td className="r num">{p.gia}{shown(c).gia === '' ? ' (gốc)' : ''}</td>
                <td className="r num">{c.rate_ap_dung}</td><td className="r num">{p.rate}{shown(c).rate === '' ? ' (gốc)' : ''}</td></tr>;
            })}</tbody>
          </table>
          <TextArea id="rate-reason" labelText="Lý do" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
          <InlineError error={isStale(m.error) ? null : m.error} />
        </div>
      </Modal>
    </section>
  );
}

function Limits({ d }: { d: Data }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const [blocked, setBlocked] = useState<Blocked[] | null>(null);
  const [caps, setCaps] = useState<Cap[] | null>(null);
  const [base, setBase] = useState<number | null>(null);
  const [done, setDone] = useState<Command | null>(null);
  const b = blocked ?? d.blocked;
  const c = caps ?? d.caps;
  const dirty = blocked !== null || caps !== null;
  const touch = () => setBase(base ?? d.meta.limits.version);
  const m = useMutation({
    mutationFn: () => api.put<{ command: Command }>('/api/admin/lode/config/limits', { chanSo: b, tranSo: c, expect: base ?? d.meta.limits.version }),
    onSuccess: (r) => { setDone(r.command); setBlocked(null); setCaps(null); setBase(null); qc.invalidateQueries({ queryKey: ['lode-config'] }); },
  });
  const editable = can('lode.manage');
  // cap only counts single-number bet types (bao lo, de, 3 cang...): he A sums stake x price per number
  const singleTypes = [...new Map(d.categories.filter((x) => x.max_number === 0).map((x) => [x.code, x])).values()];
  const allTypes = [...new Map(d.categories.map((x) => [x.code, x])).values()];
  const stationSelect = (id: string, v: number, on: (n: number) => void) => (
    <Select id={id} size="sm" hideLabel labelText="Đài" value={String(v)} disabled={!editable} onChange={(e) => on(Number(e.target.value))}>
      <SelectItem value="0" text="Mọi đài" />
      {d.stations.map((s) => <SelectItem key={s.id} value={String(s.id)} text={s.name === REGION[s.region] ? s.name : `${s.name} (${REGION[s.region]})`} />)}
    </Select>
  );
  const typeSelect = (id: string, v: string, list: LodeCategory[], on: (s: string) => void) => (
    <Select id={id} size="sm" hideLabel labelText="Loại cược" value={v} disabled={!editable} onChange={(e) => on(e.target.value)}>
      <SelectItem value="" text="Mọi loại" />
      {list.map((x) => <SelectItem key={x.code} value={x.code} text={`${x.name} (${x.code})`} />)}
    </Select>
  );
  const badNum = (s: string) => !/^\d{1,4}$/.test(s);
  const invalid = b.some((x) => badNum(x.so)) || c.some((x) => !(x.toiDa > 0));
  return (
    <section className="section">
      <h2>Số chặn và trần tiền một số</h2>
      <p className="note">Hệ A kiểm tra trong cùng giao dịch trừ tiền của lượt cược: số bị chặn không nhận cược; tổng tiền cược tính trên một số (tiền một số nhân giá) của một đài, một ngày, một loại không vượt trần. Trần chỉ áp dụng cho loại cược một số (lô, đề, càng); nhiều trần cùng khớp thì lấy trần nhỏ nhất. Lưu là áp dụng ngay.
        {d.meta.limits.at ? <> Sửa lần cuối <When v={d.meta.limits.at} />.</> : null}</p>
      {done ? <InlineNotification kind={done.status === 'SUCCEEDED' ? 'success' : 'warning'} lowContrast onClose={() => setDone(null)}
        title={done.status === 'SUCCEEDED' ? 'Hệ A đã nhận danh sách mới' : 'Lệnh chưa xong'}
        subtitle={done.status === 'SUCCEEDED' ? '' : (done.lastError && HE_A_ERRORS[done.lastError]) || done.lastError || done.status} /> : null}
      {isStale(m.error) ? <InlineNotification kind="warning" lowContrast hideCloseButton title="Danh sách vừa được người khác đổi" subtitle="Chưa lưu gì. Bỏ thay đổi, tải lại và sửa lại." /> : null}
      <h3 className="lode-h3">Số chặn ({b.length})</h3>
      <table className="plain vis">
        <thead><tr><th>Số</th><th>Đài</th><th>Loại cược</th><th>Lý do</th><th /></tr></thead>
        <tbody>
          {b.map((x, i) => {
            const set = (p: Partial<Blocked>) => { touch(); setBlocked(b.map((y, j) => (j === i ? { ...y, ...p } : y))); };
            return (
              <tr key={i}>
                <td><TextInput id={`bs-${i}`} size="sm" hideLabel labelText="Số" inputMode="numeric" className="money-input money-input--short" value={x.so} disabled={!editable}
                  invalid={badNum(x.so)} invalidText="1 đến 4 chữ số" onChange={(e) => set({ so: e.target.value.replace(/\D/g, '').slice(0, 4) })} /></td>
                <td data-label="Đài">{stationSelect(`bd-${i}`, x.dai, (n) => set({ dai: n }))}</td>
                <td data-label="Loại cược">{typeSelect(`bm-${i}`, x.ma, allTypes, (s) => set({ ma: s }))}</td>
                <td data-label="Lý do"><TextInput id={`bl-${i}`} size="sm" hideLabel labelText="Lý do" value={x.lyDo ?? ''} disabled={!editable} maxLength={120}
                  onChange={(e) => set({ lyDo: e.target.value })} /></td>
                <td>{editable ? <Button kind="ghost" size="sm" hasIconOnly renderIcon={TrashCan} iconDescription="Bỏ số chặn" onClick={() => { touch(); setBlocked(b.filter((_, j) => j !== i)); }} /> : null}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {editable ? <Button kind="ghost" size="sm" renderIcon={Add} onClick={() => { touch(); setBlocked([...b, { dai: 0, ma: '', so: '' }]); }}>Thêm số chặn</Button> : null}
      <h3 className="lode-h3">Trần tiền một số ({c.length})</h3>
      <table className="plain vis">
        <thead><tr><th>Đài</th><th>Loại cược</th><th className="r">Trần (tổng tiền cược một số)</th><th /></tr></thead>
        <tbody>
          {c.map((x, i) => {
            const set = (p: Partial<Cap>) => { touch(); setCaps(c.map((y, j) => (j === i ? { ...y, ...p } : y))); };
            return (
              <tr key={i}>
                <td data-label="Đài">{stationSelect(`cd-${i}`, x.dai, (n) => set({ dai: n }))}</td>
                <td data-label="Loại cược">{typeSelect(`cm-${i}`, x.ma, singleTypes, (s) => set({ ma: s }))}</td>
                <td className="r" data-label="Trần"><MoneyInput id={`ct-${i}`} size="sm" hideLabel labelText="Trần" className="money-input" disabled={!editable} maxDigits={13}
                  value={x.toiDa || ''} invalid={!(x.toiDa > 0)} invalidText="Lớn hơn 0"
                  onChange={(v) => set({ toiDa: Number(v) || 0 })} /></td>
                <td>{editable ? <Button kind="ghost" size="sm" hasIconOnly renderIcon={TrashCan} iconDescription="Bỏ trần" onClick={() => { touch(); setCaps(c.filter((_, j) => j !== i)); }} /> : null}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {editable ? <Button kind="ghost" size="sm" renderIcon={Add} onClick={() => { touch(); setCaps([...c, { dai: 0, ma: '', toiDa: 0 }]); }}>Thêm trần</Button> : null}
      <InlineError error={isStale(m.error) ? null : m.error} />
      {dirty ? (
        <div className="actionbar" role="region" aria-label="Thay đổi chưa lưu">
          <span>Số chặn hoặc trần đã đổi, chưa lưu</span>
          <Button kind="ghost" size="md" onClick={() => { setBlocked(null); setCaps(null); setBase(null); m.reset(); }}>Bỏ thay đổi</Button>
          <Button kind="primary" size="md" disabled={invalid || m.isPending} onClick={() => m.mutate()}>Lưu và áp dụng</Button>
        </div>
      ) : null}
    </section>
  );
}

export function LodeConfigPage() {
  const q = useQuery({ queryKey: ['lode-config'], queryFn: () => api.get<Data>('/api/admin/lode/config') });
  return (
    <div className="page page--with-bar">
      <PageHeader title="Cấu hình lô đề" lead="Giá và tỷ lệ trả theo loại cược (lô đề 3 miền và siêu tốc), số chặn và trần tiền một số của lô đề 3 miền." />
      {q.isLoading ? <BlockSkeleton lines={10} /> : q.error || !q.data ? <ErrorState error={q.error} onRetry={() => q.refetch()} /> : <>
        <Limits d={q.data} />
        <Rates d={q.data} />
      </>}
    </div>
  );
}
